import { Municipality } from "./municipality";
import { MunicipalityRepository } from "./municipalityRepository";
import { LocalCache } from "../../core/cache/local-cache";

/** Shape bruto retornado pela API de localidades do IBGE (chaves em kebab-case). */
type MunicipalityRaw = { [key: string]: string };

const CACHE_KEY = "municipality";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

/**
 * Implementação de {@link MunicipalityRepository} usando a API de localidades do IBGE
 * e IndexedDB como cache local.
 */
export class MunicipalityRepositoryImpl implements MunicipalityRepository {
  private static readonly cache = LocalCache.getInstance(CACHE_KEY, TTL_MS);

  /**
   * Busca municípios cujo nome contenha o texto informado, ignorando acentos e capitalização.
   * @param name Nome ou parte do nome do município
   * @returns Lista de municípios correspondentes
   */
  async findByName(name: string): Promise<Municipality[]> {
    const municipalities = await this.getMunicipalities();
    return municipalities.filter((m) => {
      return this.compareStrings(m.cityName, name);
    });
  }

  /**
   * Obtém a lista de municípios do IndexedDB (cache local com TTL de 7 dias) ou da API do IBGE.
   * Persiste o resultado no cache para evitar requisições repetidas.
   * @returns Lista completa de municípios
   * @throws {Error} Quando a requisição à API do IBGE falha
   */
  private async getMunicipalities(): Promise<Municipality[]> {
    const cached = await MunicipalityRepositoryImpl.cache.get<Municipality[]>(CACHE_KEY);
    if (cached) {
      return cached;
    }
    const municipalities = await this.cleanMunicipalities();
    await MunicipalityRepositoryImpl.cache.set(CACHE_KEY, municipalities);
    return municipalities;
  }

  /**
   * Consulta a API do IBGE e mapeia os dados brutos para o modelo {@link Municipality}.
   * @returns Lista de municípios mapeados para o modelo da aplicação
   * @throws {Error} Quando a requisição à API falha
   */
  private async cleanMunicipalities() {
    const response = await fetch(
      "https://servicodados.ibge.gov.br/api/v1/localidades/municipios?view=nivelado&orderBy=nome"
    );
    if (!response.ok) {
      throw new Error("Falha na consulta à API");
    }
    const municipalities: MunicipalityRaw[] = await response.json();
    return municipalities.map((m: MunicipalityRaw) => {
      return new Municipality(
        m["municipio-id"],
        m["municipio-nome"],
        m["microrregiao-id"],
        m["microrregiao-nome"],
        m["mesorregiao-id"],
        m["mesorregiao-nome"],
        m["regiao-imediata-id"],
        m["regiao-imediata-nome"],
        m["regiao-intermediaria-id"],
        m["regiao-intermediaria-nome"],
        m["UF-id"],
        m["UF-sigla"],
        m["UF-nome"],
        m["regiao-id"],
        m["regiao-sigla"],
        m["regiao-nome"]
      );
    });
  }

  /**
   * Compara duas strings ignorando acentos diacríticos e diferenças de maiúsculas/minúsculas.
   * @param str1 String de referência
   * @param str2 String a ser buscada
   * @returns `true` se `str1` contiver `str2` após normalização
   */
  private compareStrings(str1: string, str2: string): boolean {
    const removeSpecialChars = (str: string): string => {
      return str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[ç]/g, "c")
        .toUpperCase();
    };
    return removeSpecialChars(str1).includes(removeSpecialChars(str2));
  }
}
